import React from 'react';
import { ProTable, Link, DateDisplay } from 'components/layout';
import { useMutation, useQuery } from '@apollo/client';
import { RollbackOutlined } from '@ant-design/icons';
import fp from 'lodash/fp';
import {
  DELETE_PROJECT_APPLICATION,
  GET_MY_PROJECT_APPLICATIONS,
  WITHDRAW_PROJECT_APPLICATION,
} from 'gql/projectApplications';
import {
  stringSorter,
  filterByTitle,
  filterByFunding,
  filterByStatus,
} from 'utils';
import { DeleteButton, ShowConfirmButton } from 'components/button';
import { getFunding } from './utils';
import { ENUM_PROJECT_APPLICATION_STATUS } from 'constants/form';

const MyProjectApplications = () => {
  const { data, loading } = useQuery(GET_MY_PROJECT_APPLICATIONS);
  const [deleteProjectApplication] = useMutation(DELETE_PROJECT_APPLICATION, {
    refetchQueries: [{ query: GET_MY_PROJECT_APPLICATIONS }],
  });
  const [withdrawProjectApplication] = useMutation(
    WITHDRAW_PROJECT_APPLICATION,
    {
      refetchQueries: [{ query: GET_MY_PROJECT_APPLICATIONS }],
    }
  );

  const onDelete = async (id) => {
    await deleteProjectApplication({
      variables: {
        id,
      },
    });
  };
  const onWithdraw = async (id) => {
    await withdrawProjectApplication({
      variables: {
        id,
      },
    });
  };

  const columns = [
    {
      title: 'Funding',
      key: 'funding',
      valueType: 'select',
      valueEnum: getFunding(data?.myProjectApplications),
      width: '20%',
      colSize: 2,
      dataIndex: ['setting', 'title'],
      render: (_, { id }) => (
        <Link to={`/project-applications/${id}`}>{_}</Link>
      ),
    },
    {
      title: 'Application submission date',
      key: 'submissionDate',
      responsive: ['md'],
      sorter: (a, b) => a.firstSubmissionDate - b.firstSubmissionDate,
      render: ({ firstSubmissionDate, id }) => (
        <Link to={`/project-applications/${id}`}>
          <DateDisplay date={firstSubmissionDate} />
        </Link>
      ),
      hideInSearch: true,
    },
    {
      title: 'Application deadline',
      key: 'endDate',
      // responsive: ['md'],
      sorter: (a, b) => a.setting?.endDate - b.setting?.endDate,
      render: ({ setting, id }) => (
        <>
          <Link to={`/project-applications/${id}`}>
            <DateDisplay date={setting.endDate} />
          </Link>
        </>
      ),
      hideInSearch: true,
    },
    {
      title: 'Title',
      dataIndex: 'title',
      width: '30%',
      sorter: (a, b) => stringSorter(a.title, b.title),
      render: (_, { id }) => (
        <Link to={`/project-applications/${id}`}>{_}</Link>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      valueType: 'select',
      valueEnum: ENUM_PROJECT_APPLICATION_STATUS,
      initialValue: 'ACTIVE',
      sorter: (a, b) => stringSorter(a.status, b.status),
      render: (_, { id }) => (
        <Link to={`/project-applications/${id}`}>{_}</Link>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      hideInSearch: true,
      render: (_, { id, status }) => {
        if (status === 'APPROVED') {
          return [
            <ShowConfirmButton
              key={`withdraw-${id}`}
              type="primary"
              onOk={() => onWithdraw(id)}
              content="Do you want to withdraw this application?"
              label="Withdraw"
              title="Confirm to withdraw?"
            >
              <RollbackOutlined />
              Withdraw
            </ShowConfirmButton>,
          ];
        }
        if (status === 'DRAFT') {
          return [
            <DeleteButton
              key={`delete-${id}`}
              onOk={() => onDelete(id)}
              content="Do you want to delete this application?"
            />,
          ];
        }
        return null;
      },
    },
  ];

  return (
    <ProTable
      loading={loading}
      columns={columns}
      options={{
        density: false,
      }}
      params={{ data }}
      request={async (params) => {
        // console.log({ params });
        const titleFilter = filterByTitle(params?.title);
        const statusFilter = filterByStatus(params?.status);
        const fundingFilter = filterByFunding(params?.funding);
        const filters = fp.pipe(titleFilter, fundingFilter, statusFilter);
        const result = filters(data?.myProjectApplications);
        return Promise.resolve({
          data: result,
          success: true,
        });
      }}
      search={{
        searchText: 'Search',
        labelWidth: 'auto',
        defaultCollapsed: false,
      }}
      pagination={{ defaultPageSize: 20, hideOnSinglePage: true }}
      rowKey="id"
      scroll={false}
      // dataSource={data?.myProjectApplications}
    />
  );
};

export default MyProjectApplications;
